<template>
  <div
    class="node"
    :class="
      [
        selected(),
        node.name,
        hasWarning ? 'warning' : '',
        hasError ? 'error' : '',
        isDisabled ? 'disabled' : '',
      ] | kebab
    "
  >
    <div class="title">
      <img class="icon" src="../assets/icons/check.svg">
      <div class="title-text">
        <span>{{ titleText }}</span>
        <md-tooltip 
          v-if="shouldMountComplexElements()" 
          md-direction="top"
          data-lod-complex="tooltip"
        >
          Check:<br>
          <span
            v-for="info in state.checkInfo"
            :key="info.key"
          >
            {{ tooltipLabel(info) }}
            <br>
          </span>
        </md-tooltip>
      </div>

      <help-tooltip 
        v-if="shouldMountComplexElements() && hasWarning" 
        type="warning"
        data-lod-complex="warning-tooltip"
      >
        <span v-for="warning in warnings" :key="warning.message">
          {{ warning.message }}
          <br>
        </span>
      </help-tooltip>
      <help-tooltip 
        v-if="shouldMountComplexElements() && hasError" 
        type="error"
        data-lod-complex="error-tooltip"
      >
        <span v-for="error in errors" :key="error.message">
          {{ error.message }}
          <br>
        </span>
      </help-tooltip>

      <div 
        v-if="shouldMountComplexElements() && editor.isAdmin" 
        class="nodeId"
        data-lod-complex="node-id"
      >
        ID:{{ node.id }}
      </div>
    </div>

    <div 
      v-if="shouldMountComplexElements()"
      data-lod-complex="controls-container"
    >
      <div v-for="control in controls()" :key="control.key">
        <div v-control="control" class="control" data-lod-complex="control-item">
          <el-button
            class="remove-control-btn"
            icon="circle-cross"
            type="danger"
            :disabled="state.readonly"
            data-lod-complex="remove-button"
            @click="removeControl(control)"
          />
        </div>
      </div>

      <md-menu
        md-close-on-click
        md-direction="bottom-start"
        class="check-controls-dropdown"
        data-lod-complex="controls-dropdown"
      >
        <segmented-select
          v-if="!controls().length || state.newCheckInputShown"
          :items="availableControls()"
          :value="state.activeControlType"
          segment-by="superCategory"
          :continuous-columns="true"
          :row-count="14"
          placeholder="If..."
          :disabled="state.readonly"
          :style="
            controls().length ? { marginLeft: '18px', marginRight: '18px' } : null
          "
          data-lod-complex="segmented-select"
          @select="control => addControl(control.value)"
        />
        <md-button
          v-else
          class="new-check-button md-primary md-raised"
          :disabled="state.readonly"
          data-lod-complex="new-check-button"
          @click="showNewCheckInput()"
        >
          <md-icon>add</md-icon>
          Check
        </md-button>
      </md-menu>
    </div>

    <div class="sockets-container">
      <div class="input-sockets">
        <div v-for="input in inputs()" :key="input.key" class="input">
          <Socket
            v-socket:input="input"
            class="socket"
            type="input"
            :socket="input.socket"
          />
          <div v-show="!input.showControl()" class="input-title">
            {{ input.name }}
          </div>
          <div
            v-show="input.showControl()"
            v-control="input.control"
            class="input-control"
          />
        </div>
      </div>
      <div class="output-sockets">
        <div
          v-for="output in outputs()"
          :key="output.key"
          class="output"
          :show-timeout="0"
          :hide-timeout="0"
        >
          <div class="output-title">{{ output.name }}</div>
          <Socket
            v-socket:output="output"
            class="socket"
            type="output"
            :socket="output.socket"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReteVueRenderPlugin from 'rete-vue-render-plugin';

import Chance from 'chance';
import forEach from 'lodash/forEach';
import uniqBy from 'lodash/uniqBy';
import fromPairs from 'lodash/fromPairs';
import toPairs from 'lodash/toPairs';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import toArray from 'lodash/toArray';
import sortBy from 'lodash/sortBy';

import HelpTooltip from '../../common/components/HelpTooltip';
import SegmentedSelect from '../components/SegmentedSelect.vue';
import CHECK_CONTROLS from '../CHECK_CONTROLS';
import {
  getControlForType,
  getControlDescriptionForType,
} from '../utils/getControlForType';
import getRecommendedControls from '../utils/getRecommendedControls';

const chance = new Chance();

export default {
  name: 'check',
  components: {
    Socket: ReteVueRenderPlugin.Socket,
    HelpTooltip,
    SegmentedSelect,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      // Some controls are duplicated for different check types
      // (e.g. MONITOR_VITALS and MONITOR_LEADS)
      UNIQUE_CHECK_CONTROLS: fromPairs(
        uniqBy(toPairs(CHECK_CONTROLS), pair => pair[1].label),
      ),
      state: {
        readonly: false,
        hasInputConnection: true,
        hasOutputConnection: true,
        newCheckInputShown: false,
        controlCount: 0,
        checkInfo: [],
      },
    };
  },
  computed: {
    titleText() {
      return this.state.checkInfo.length ? this.state.checkInfo[0].description.label : 'Check';
    },
    hasWarning() {
      return this.warnings && this.warnings.length;
    },
    hasError() {
      return this.errors && this.errors.length;
    },
    isDisabled() {
      for (const control of this.controls()) {
        const { checkType} = control.props;
        const descriptor = CHECK_CONTROLS[checkType];
        if (descriptor && descriptor.isDisabled && descriptor.isDisabled(this.editor.caseConf)) {
          return true;
        }
      }
      return false;
    },
    isValid() {
      return this.state.hasOutputConnection && this.state.hasInputConnection;
    },
    warnings() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    errors() {
      if (!this.editor.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.editor.$store.getters['validation/errorsByFieldAndId'],
          `caseLogicDescription.${this.node.id}`,
        ),
        'message',
      );
    },
    chainableComponents() {
      return intersection(
        ['Check', 'Effect', 'Timer', 'EffectChain'],
        toArray(this.editor.components.keys()),
      );
    },
  },
  created() {
    const { data } = this.node;
    // Add controls in displayIndex order
    const items = [];
    forEach(data, (check, checkId) => {
      items.push({
        checkId,
        check,
      });
    });
    // Remove all controls from the Rete Node
    // in case of a component remount (history undo, HMR)
    this.node.controls.forEach(control => {
      this.node.removeControl(control);
    });
    const sortedItems = sortBy(items, item => item.check.displayIndex);
    for (const item of sortedItems) {
      if (this.node.controls.get(item.checkId)) {
        this.node.removeControl(this.node.controls.get(item.checkId));
      }
      this.addControl(item.check.type, item.checkId, item.check);
    }
    this.editor.on('readonly', readonly => {
      this.state.readonly = readonly;
    });
    this.editor.trigger('process', this.node);
  },
  beforeDestroy() {
    this.node.controls.forEach(control => {
      // Rete does not delete vue instances when node is destroyed
      control._vue.$destroy();
    });
  },
  methods: {
    // LOD methods
    isLodSimplified() {
      return this.node._lodState ? this.node._lodState.isSimplified : false;
    },
    shouldRenderComplexElements() {
      return !this.isLodSimplified();
    },
    hasBeenRenderedComplex() {
      return this.node._lodState ? this.node._lodState.hasBeenRenderedComplex : false;
    },
    useHybridRendering() {
      return this.node._lodState ? this.node._lodState.useHybridRendering : false;
    },
    // Hybrid rendering method: use v-if initially, then v-show after first complex render
    shouldMountComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering, mount elements if they've been rendered complex at least once
      return this.hasBeenRenderedComplex();
    },
    shouldShowComplexElements() {
      if (!this.useHybridRendering()) {
        return this.shouldRenderComplexElements();
      }
      // If using hybrid rendering and elements are mounted, show/hide via CSS
      return this.hasBeenRenderedComplex() && this.shouldRenderComplexElements();
    },
    availableControls() {
      return sortBy(
        getRecommendedControls(
          this.editor.caseConf,
          this.UNIQUE_CHECK_CONTROLS,
          this.editor.isGlobal,
        )
        .filter(control => !control.isDisabled || !control.isDisabled(this.editor.caseConf))
        .map(control => ({
          ...control,
          value: control.key,
          class: control.recommended
            ? 'control-option recommended'
            : 'control-option',
        })),
        control =>
          [
            'Dialogue',
            'Assessment',
            'IV',
            'Medications',
            'Oxygen',
            'Procedures',
            'Monitors',
            'Miscellaneous',
          ].indexOf(control.superCategory),
      );
    },
    addControl(checkType, checkId = `check-${chance.guid()}`, check) {
      const CheckControl = getControlForType(checkType, check, CHECK_CONTROLS);
      const newControl = new CheckControl(this.editor, checkId);
      // Add displayIndex so that display order can be preserved for
      // checks that save it (ex. DialogueChecks)
      newControl.displayIndex = this.state.controlCount++;

      this.node.addControl(newControl);
      this.$forceUpdate();
      this.editor.trigger('process', this.node);
      this.state.newCheckInputShown = false;
      const checkControlDescription = getControlDescriptionForType(
        checkType,
        check,
        CHECK_CONTROLS,
      );
      if (!check) {
        if (!this.editor.isGlobal) {
          this.$analytics.event(
            'Authoring Tool',
            'check_added',
            checkControlDescription && checkControlDescription.title,
          );
        }
      }
      const info = {
        key: checkId,
        check,
        description: checkControlDescription
      };
      this.state.checkInfo.push(info);
    },
    removeControl(control) {
      this.node.removeControl(control);
      if (control._vue) {
        control._vue.$destroy();
      }
      delete this.node.data[control.key];
      const infoIndex = this.state.checkInfo.findIndex(info => info.key === control.key);
      if (infoIndex >= 0) {
        this.state.checkInfo.splice(infoIndex, 1);
      }
      this.$forceUpdate();
      this.editor.trigger('process', this.node);
    },
    showNewCheckInput() {
      this.state.newCheckInputShown = true;
    },
    tooltipLabel(checkInfo) {
      const { check, description } = checkInfo;
      return description.checkSpecificLabel ?
        description.checkSpecificLabel(this.editor.caseConf, check) :
        description.label;
    },
  },
};
</script>

<style scoped>
.node {
  background: rgb(125, 48, 201);
  border-radius: 10px;
  cursor: pointer;
  min-width: 620px;
  padding-bottom: 6px;
  box-sizing: content-box;
  position: relative;
  user-select: none;
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  box-sizing: border-box;
}

.node:hover {
  background: rgba(125, 48, 201, 0.8);
}

.node.highlighted {
  background: #fbd430;
  border-color: #fbd430;
}

.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
}

.node.disabled {
  background: #525252;
}

.node .icon {
  width: 16px;
  height: 16px;
  display: inline;
}

.node .title {
  color: white;
  font-family: sans-serif;
  font-size: 18px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.node .title .title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  padding-left: 4px;
}

.node .nodeId {
  float: right;
}

.sockets-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.node .socket.input {
  background: url('../assets/icons/active-input.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  margin-left: 0;
}

.node .socket.output {
  background: url('../assets/icons/active-output.svg');
  background-size: 100%;
  background-repeat: no-repeat;
  margin-right: 0;
}

.node .socket {
  border: none;
}

.node .socket:hover {
  border: solid;
}

.node .output {
  text-align: right;
}

.node .input {
  text-align: left;
}

.node .input-title,
.node .output-title {
  vertical-align: middle;
  color: white;
  display: inline-block;
  font-family: sans-serif;
  font-size: 14px;
  margin: 6px;
  line-height: 24px;
}

.node .input-control {
  z-index: 1;
  width: calc(100% - 36px);
  vertical-align: middle;
  display: inline-block;
}

.node .control {
  padding: 6px 18px;
}

.check-controls-dropdown {
  text-align: center;
  width: 100%;
}

.check-controls-dropdown-menu {
  transition-duration: 0s;
}

.control {
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
  position: relative;
  margin-bottom: 20px;
}

.remove-control-btn {
  margin-left: 10px;
}

.node.error {
  border: solid 3px #f92c2d;
}

.node.warning {
  border: solid 3px #f2994a;
}

.new-check-button {
  margin-top: 10px;
}

.control-option >>> .md-list-item-content {
  min-height: 40px;
  cursor: pointer;
}

.control-option:hover >>> .md-list-item-content {
  background-color: #e4e5f1;
}

.node >>> .control-option:not(.recommended) {
  color: #a9b3bc;
}
</style>
